/**
 * Lot P « Dépenses réelles » (décisions de Maxime du 13/10/2026) : logique pure, testée.
 *
 * - « Pas une dépense » : règles par défaut des lignes bancaires (virements internes, TVA reversée) ;
 * - doublons : une ligne bancaire non rapprochée qui reprend une dépense Axonaut (même montant TTC, ±5 jours) ;
 * - règles apprises (libellé ou fournisseur → poste) et préséance des origines d'un poste ;
 * - TVA : les lignes bancaires sont TTC, comptées HT pour les postes à TVA récupérable ;
 * - estimation des mois restants sans Claude (moyenne des 3 derniers mois complets, ou même mois de l'an
 *   dernier pour un poste nettement saisonnier) et projection de fin d'année.
 */

// ------------------------------------------------------------------ Libellés

/** Libellé normalisé : majuscules sans accents, ponctuation remplacée par des espaces. */
export function normalizeLabel(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

/** Mots d'un libellé, sans ceux qui contiennent un chiffre (dates, références, numéros de virement). */
export function labelWords(text: string | null | undefined): string[] {
  return normalizeLabel(text)
    .split(' ')
    .filter((w) => w !== '' && !/\d/.test(w));
}

// ------------------------------------------------------------------ Pas une dépense

export const EXCLUSION_REASONS = ['internal_transfer', 'vat_payment', 'refund', 'other'] as const;
export type ExclusionReason = (typeof EXCLUSION_REASONS)[number];

export const EXCLUSION_LABELS: Record<ExclusionReason, string> = {
  internal_transfer: 'Virement interne',
  vat_payment: 'TVA reversée',
  refund: 'Remboursement',
  other: 'Autre',
};

/** « VIR AXONAUT EVOLUSITE », « VIR INST EVOLUSITE … », « VIR INST AXONAUT EVOLUSITE … ». */
const INTERNAL_TRANSFER = /\bVIR(?: INST)?(?: AXONAUT)? EVOLUSITE\b/;
/** Un salaire ou des indemnités kilométriques versés par virement restent des dépenses. */
const NOT_A_TRANSFER = /\b(SALAIRES?|IK)\b/;
/** « TVA1-…-3310CA3 DGFIP / SIE … » : la TVA collectée reversée (le CA est suivi HT). */
const VAT = /\bTVA/;
const VAT_AUTHORITY = /3310CA3|\bDGFIP\b|\bSIE\b/;

/**
 * Règle par défaut, appliquée par la synchro aux lignes bancaires : virement interne ou TVA reversée, sinon
 * null. Le résultat reste visible et réversible dans l'écran (jamais supprimé).
 */
export function defaultExclusion(label: string | null | undefined): ExclusionReason | null {
  const text = normalizeLabel(label);
  if (INTERNAL_TRANSFER.test(text) && !NOT_A_TRANSFER.test(text)) return 'internal_transfer';
  if (VAT.test(text) && VAT_AUTHORITY.test(text)) return 'vat_payment';
  return null;
}

// ------------------------------------------------------------------ Doublons

export const DUPLICATE_WINDOW_DAYS = 5;

/** Mots trop courants dans les libellés bancaires pour départager deux fournisseurs. */
const GENERIC_WORDS = new Set([
  'VIR',
  'INST',
  'SEPA',
  'PRLV',
  'PRELEVEMENT',
  'CB',
  'CARTE',
  'FACTURE',
  'FACT',
  'PAIEMENT',
  'ECH',
  'REF',
  'DU',
  'DE',
  'LA',
  'LE',
  'LES',
  'ET',
  'FR',
  'SAS',
  'SARL',
  'EURL',
  'SA',
]);

function significantWords(text: string): Set<string> {
  return new Set(labelWords(text).filter((w) => w.length >= 2 && !GENERIC_WORDS.has(w)));
}

/** Ressemblance de deux libellés (indice de Jaccard sur les mots significatifs), de 0 à 1. */
export function textSimilarity(a: string, b: string): number {
  const x = significantWords(a);
  const y = significantWords(b);
  if (x.size === 0 || y.size === 0) return 0;
  let common = 0;
  for (const w of x) {
    if (
      y.has(w) ||
      [...y].some((v) => v.length >= 4 && w.length >= 4 && (v.startsWith(w) || w.startsWith(v)))
    )
      common += 1;
  }
  return common / (x.size + y.size - common);
}

function dayNumber(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
}

export interface BankLineCandidate {
  id: string;
  date: string;
  /** Montant de l'opération (TTC), positif. */
  amountCents: number;
  /** Libellé et contrepartie. */
  text: string;
}

export interface AxonautExpenseCandidate {
  id: string;
  date: string;
  /** Total TTC de la dépense. */
  totalTtcCents: number;
  /** Titre et fournisseur. */
  text: string;
}

/**
 * Lignes bancaires non rapprochées qui doublent une dépense Axonaut : même montant TTC (au centime), dates à
 * ±5 jours. Une dépense ne peut être doublée qu'une fois ; entre plusieurs candidats, la ressemblance du
 * fournisseur l'emporte, puis la date la plus proche. Résultat : identifiant de la ligne → dépense.
 */
export function matchDuplicates(
  bank: readonly BankLineCandidate[],
  expenses: readonly AxonautExpenseCandidate[],
  windowDays = DUPLICATE_WINDOW_DAYS,
): Map<string, string> {
  const byAmount = new Map<number, AxonautExpenseCandidate[]>();
  for (const e of expenses) {
    if (e.totalTtcCents <= 0) continue;
    const list = byAmount.get(e.totalTtcCents) ?? [];
    list.push(e);
    byAmount.set(e.totalTtcCents, list);
  }
  const pairs: { bank: string; expense: string; similarity: number; gap: number }[] = [];
  for (const line of bank) {
    if (line.amountCents <= 0) continue;
    for (const e of byAmount.get(line.amountCents) ?? []) {
      const gap = Math.abs(dayNumber(line.date) - dayNumber(e.date));
      if (gap > windowDays) continue;
      pairs.push({ bank: line.id, expense: e.id, similarity: textSimilarity(line.text, e.text), gap });
    }
  }
  pairs.sort(
    (a, b) =>
      b.similarity - a.similarity ||
      a.gap - b.gap ||
      a.bank.localeCompare(b.bank) ||
      a.expense.localeCompare(b.expense),
  );
  const links = new Map<string, string>();
  const used = new Set<string>();
  for (const pair of pairs) {
    if (links.has(pair.bank) || used.has(pair.expense)) continue;
    links.set(pair.bank, pair.expense);
    used.add(pair.expense);
  }
  return links;
}

// ------------------------------------------------------------------ Règles apprises et préséance

export const CATEGORY_SOURCES = ['user', 'rule', 'account_code', 'ai', 'import'] as const;
export type CategorySource = (typeof CATEGORY_SOURCES)[number];

export type RuleField = 'label' | 'counterparty';

export interface ExpenseRuleLike {
  id: string;
  field: RuleField;
  pattern: string;
  categoryId: string;
}

/** Motif d'une règle tel qu'il est enregistré : mots du libellé, sans les nombres. Vide = invalide. */
export function rulePattern(text: string): string {
  return labelWords(text).join(' ');
}

/** Les mots du motif se suivent dans le texte (nombres ignorés des deux côtés). */
export function patternMatches(pattern: string, text: string | null | undefined): boolean {
  const p = labelWords(pattern);
  if (p.length === 0) return false;
  const t = labelWords(text);
  for (let i = 0; i + p.length <= t.length; i += 1) {
    if (p.every((w, k) => t[i + k] === w)) return true;
  }
  return false;
}

/** Règle qui s'applique à une ligne : le motif le plus long l'emporte (le plus précis), puis le plus ancien. */
export function matchRule<R extends ExpenseRuleLike>(
  rules: readonly R[],
  row: { label: string; counterparty: string | null },
): R | null {
  let best: R | null = null;
  for (const rule of rules) {
    const text = rule.field === 'label' ? row.label : row.counterparty;
    if (!patternMatches(rule.pattern, text)) continue;
    if (!best || labelWords(rule.pattern).length > labelWords(best.pattern).length) best = rule;
  }
  return best;
}

/**
 * Règle proposée après une recatégorisation (« Appliquer aux opérations similaires ») : le fournisseur s'il est
 * connu, sinon les trois premiers mots du libellé (hors nombres). Modifiable par la personne.
 */
export function suggestRule(row: {
  label: string;
  counterparty: string | null;
}): { field: RuleField; pattern: string } | null {
  const supplier = rulePattern(row.counterparty ?? '');
  if (supplier) return { field: 'counterparty', pattern: supplier };
  const words = labelWords(row.label).slice(0, 3);
  return words.length > 0 ? { field: 'label', pattern: words.join(' ') } : null;
}

export interface CategoryDecision {
  categoryId: string | null;
  source: CategorySource | null;
  confidence: number | null;
}

/**
 * Poste d'une ligne selon la préséance vous > règle > compte comptable > IA : un choix humain n'est jamais
 * remplacé ; sinon une règle apprise, puis le compte comptable du poste ; sinon la prédiction de l'IA déjà
 * posée est gardée ; sinon « à catégoriser ».
 */
export function resolveCategory(input: {
  current: CategoryDecision;
  ruleCategoryId: string | null;
  accountCategoryId: string | null;
}): CategoryDecision {
  const { current } = input;
  if (current.source === 'user' || current.source === 'import') return current;
  if (input.ruleCategoryId) return { categoryId: input.ruleCategoryId, source: 'rule', confidence: null };
  if (input.accountCategoryId)
    return { categoryId: input.accountCategoryId, source: 'account_code', confidence: null };
  if (current.source === 'ai' && current.categoryId) return current;
  return { categoryId: null, source: null, confidence: null };
}

/** En dessous de ce seuil, une prédiction de l'IA apparaît dans « À vérifier ». */
export const AI_REVIEW_THRESHOLD = 0.7;

// ------------------------------------------------------------------ TVA

export const VAT_RATE = 0.2;

/** Postes sans TVA récupérable : salaires, URSSAF et impôts, IK, mutuelles et RC Pro, frais bancaires. */
const NO_VAT_KEYS = new Set([
  'salaires',
  'urssaf-impots',
  'indemnites-kilometriques',
  'mutuelles-rc-pro',
  'frais-bancaires',
]);
const NO_VAT_NAME = /salaire|urssaf|impot|indemnit|kilom|mutuelle|assurance|frais bancaire|cotisation/;

/** Valeur par défaut de « TVA récupérable » d'un poste (modifiable ensuite). */
export function defaultVatRecoverable(key: string, name: string): boolean {
  if (NO_VAT_KEYS.has(key)) return false;
  const text = name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return !NO_VAT_NAME.test(text);
}

/**
 * Montant compté dans les totaux : une ligne bancaire (TTC) d'un poste à TVA récupérable est ramenée au HT
 * (÷ 1,2) ; les dépenses Axonaut sont déjà HT ; une ligne sans poste reste TTC (TVA inconnue).
 */
export function countedCents(amountCents: number, isTaxIncluded: boolean, vatRecoverable: boolean): number {
  return isTaxIncluded && vatRecoverable ? Math.round(amountCents / (1 + VAT_RATE)) : amountCents;
}

// ------------------------------------------------------------------ Estimation sans Claude

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const index = y * 12 + (m - 1) + delta;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

export type FallbackSource = 'average' | 'seasonal';

/**
 * Poste nettement saisonnier sur les 12 mois précédant `current` : dépensé au plus 4 mois sur 12, ou un mois
 * pèse au moins la moitié de l'année (assurance annuelle, salon…). Il faut un historique d'environ un an (une
 * dépense dans les 3 premiers mois de la fenêtre) : un poste récent prend la moyenne.
 */
export function isSeasonal(actual: (month: string) => number, current: string): boolean {
  const values = Array.from({ length: 12 }, (_, i) => actual(shiftMonth(current, -12 + i)));
  const total = values.reduce((s, v) => s + v, 0);
  if (total <= 0) return false;
  if (!values.slice(0, 3).some((v) => v > 0)) return false;
  const nonZero = values.filter((v) => v > 0).length;
  return nonZero <= 4 || Math.max(...values) >= total / 2;
}

/**
 * Estimation d'un mois (mois en cours ou à venir) sans Claude : même mois de l'an dernier pour un poste nettement
 * saisonnier, sinon moyenne des 3 derniers mois complets.
 */
export function fallbackEstimate(
  actual: (month: string) => number,
  target: string,
  current: string,
): { cents: number; source: FallbackSource } {
  if (isSeasonal(actual, current))
    return { cents: Math.max(0, actual(shiftMonth(target, -12))), source: 'seasonal' };
  const last = [1, 2, 3].map((n) => actual(shiftMonth(current, -n)));
  return { cents: Math.max(0, Math.round(last.reduce((s, v) => s + v, 0) / 3)), source: 'average' };
}

/**
 * Projection de fin d'année d'un poste : réel des mois écoulés et du mois en cours, plus l'estimation de ce qui
 * reste (mois en cours : estimation − déjà dépensé, jamais négatif ; mois suivants : estimation).
 */
export function yearEndOfCategory(
  months: readonly { month: string; actualCents: number; estimateCents: number }[],
  current: string,
): { actualCents: number; estimatedCents: number } {
  let actualCents = 0;
  let estimatedCents = 0;
  for (const m of months) {
    if (m.month < current) actualCents += m.actualCents;
    else if (m.month === current) {
      actualCents += m.actualCents;
      estimatedCents += remainingEstimate(m.estimateCents, m.actualCents);
    } else estimatedCents += m.estimateCents;
  }
  return { actualCents, estimatedCents };
}

// ------------------------------------------------------------------ Lot Y : estimation saisie dans le tableau

/**
 * Part de l'estimation qui reste à dépenser (lot Y [11]) : la saisie est le total du mois ; ce qui compte en plus
 * du réel déjà reçu est `estimation − réel`, jamais négatif. Mois à venir : réel nul, l'estimation entière.
 */
export function remainingEstimate(estimateCents: number, actualCents: number): number {
  return Math.max(0, estimateCents - actualCents);
}

/** Case d'un mois en prévision : réel déjà reçu + ce qui reste selon l'estimation (= max(estimation, réel)). */
export function monthTotalWithEstimate(actualCents: number, estimateCents: number): number {
  return actualCents + remainingEstimate(estimateCents, actualCents);
}

// ------------------------------------------------------------------ Lot Y : « À catégoriser » regroupé

/** Une contrepartie forme un groupe à partir de 2 lignes ; en dessous, la ligne reste isolée. */
export const COUNTERPARTY_GROUP_MIN = 2;

export interface GroupableLine {
  id: string;
  date: string;
  label: string;
  amountCents: number;
  accountingCode: string | null;
  /** Libellé du compte comptable dans Axonaut, s'il est connu. */
  accountingLabel?: string | null;
  counterparty: string | null;
}

export interface CodeGroup<L> {
  code: string;
  label: string | null;
  lines: L[];
  totalCents: number;
}

export interface CounterpartyGroup<L> {
  /** Motif de la règle qui rangera les prochaines lignes (mots de la contrepartie, sans les nombres). */
  pattern: string;
  /** Contrepartie telle qu'Axonaut l'écrit (la plus fréquente du groupe). */
  counterparty: string;
  lines: L[];
  totalCents: number;
}

export interface UncategorizedGrouping<L> {
  codeGroups: CodeGroup<L>[];
  counterpartyGroups: CounterpartyGroup<L>[];
  isolated: L[];
}

const byWeight =
  <G extends { lines: unknown[]; totalCents: number }>(key: (g: G) => string) =>
  (a: G, b: G) =>
    b.lines.length - a.lines.length || b.totalCents - a.totalCents || key(a).localeCompare(key(b));

function mostFrequent(values: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  let best = values[0] ?? '';
  for (const [v, n] of counts) if (n > (counts.get(best) ?? 0)) best = v;
  return best;
}

/**
 * « À catégoriser » regroupé (lot Y [13]) : d'abord par compte comptable Axonaut (un choix range tout le compte,
 * même une seule ligne), puis, sans compte, par contrepartie (2 lignes ou plus), puis les lignes isolées (dans
 * l'ordre reçu). Groupes du plus gros au plus petit.
 */
export function groupUncategorized<L extends GroupableLine>(lines: readonly L[]): UncategorizedGrouping<L> {
  const codes = new Map<string, CodeGroup<L>>();
  const parties = new Map<string, { names: string[]; lines: L[]; totalCents: number }>();
  const rest: L[] = [];
  for (const line of lines) {
    const code = line.accountingCode?.trim();
    if (code) {
      const group = codes.get(code) ?? { code, label: null, lines: [], totalCents: 0 };
      group.lines.push(line);
      group.totalCents += line.amountCents;
      group.label ??= line.accountingLabel?.trim() || null;
      codes.set(code, group);
      continue;
    }
    const pattern = rulePattern(line.counterparty ?? '');
    if (!pattern) {
      rest.push(line);
      continue;
    }
    const group = parties.get(pattern) ?? { names: [], lines: [], totalCents: 0 };
    group.names.push(line.counterparty!.trim());
    group.lines.push(line);
    group.totalCents += line.amountCents;
    parties.set(pattern, group);
  }
  const counterpartyGroups: CounterpartyGroup<L>[] = [];
  const isolatedIds = new Set(rest.map((l) => l.id));
  for (const [pattern, group] of parties) {
    if (group.lines.length >= COUNTERPARTY_GROUP_MIN) {
      counterpartyGroups.push({
        pattern,
        counterparty: mostFrequent(group.names),
        lines: group.lines,
        totalCents: group.totalCents,
      });
    } else for (const l of group.lines) isolatedIds.add(l.id);
  }
  return {
    codeGroups: [...codes.values()].sort(byWeight((g) => g.code)),
    counterpartyGroups: counterpartyGroups.sort(byWeight((g) => g.pattern)),
    isolated: lines.filter((l) => isolatedIds.has(l.id)),
  };
}

// ------------------------------------------------------------------ Lot Y : « À vérifier » au-dessus d'un seuil

/** `settings.expenses.reviewMinCents` par défaut : 300 € (lot Y [16]). */
export const DEFAULT_REVIEW_MIN_CENTS = 30_000;
/** Choix proposés dans la carte « À vérifier » : 100 €, 300 €, 1 000 €, toutes (0). */
export const REVIEW_MIN_CHOICES = [10_000, 30_000, 100_000, 0] as const;

export interface ReviewableLine {
  id: string;
  label: string;
  amountCents: number;
  counterparty: string | null;
  categoryId: string;
  confidence: number;
}

/** Lignes à montrer dans « À vérifier » : montant ≥ seuil (0 = toutes). En dessous, rien n'est demandé. */
export function aboveReviewThreshold<L extends Pick<ReviewableLine, 'amountCents'>>(
  lines: readonly L[],
  minCents: number,
): L[] {
  return minCents <= 0 ? [...lines] : lines.filter((l) => l.amountCents >= minCents);
}

export interface ReviewGrouping<L> {
  key: string;
  /** Contrepartie, sinon « Sans contrepartie · <mots du libellé> », sinon « Sans libellé ». */
  title: string;
  categoryId: string;
  lines: L[];
  totalCents: number;
  /** Confiance moyenne de l'IA (0 à 1). */
  confidence: number;
}

/** « À vérifier » regroupé par contrepartie et poste proposé (lot Y [16]), du plus gros montant au plus petit. */
export function groupReview<L extends ReviewableLine>(lines: readonly L[]): ReviewGrouping<L>[] {
  const groups = new Map<string, { title: string; names: string[]; categoryId: string; lines: L[] }>();
  for (const line of lines) {
    const party = rulePattern(line.counterparty ?? '');
    const words = labelWords(line.label).slice(0, 3).join(' ');
    const who = party ? `c:${party}` : words ? `l:${words}` : 'none';
    const key = `${who}|${line.categoryId}`;
    const group = groups.get(key) ?? {
      title: party ? '' : words ? `Sans contrepartie · ${words}` : 'Sans libellé',
      names: [],
      categoryId: line.categoryId,
      lines: [],
    };
    if (party) group.names.push(line.counterparty!.trim());
    group.lines.push(line);
    groups.set(key, group);
  }
  return [...groups.entries()]
    .map(([key, g]) => ({
      key,
      title: g.names.length > 0 ? mostFrequent(g.names) : g.title,
      categoryId: g.categoryId,
      lines: g.lines,
      totalCents: g.lines.reduce((s, l) => s + l.amountCents, 0),
      confidence: g.lines.reduce((s, l) => s + l.confidence, 0) / g.lines.length,
    }))
    .sort(
      (a, b) => b.totalCents - a.totalCents || b.lines.length - a.lines.length || a.key.localeCompare(b.key),
    );
}
